import { ReactNode } from "react";
import Box from "@mui/material/Box";
import { alpha } from "@mui/material/styles";
import TableSortLabel from "@mui/material/TableSortLabel";
import type { SortDir } from "./useConfigList";

/**
 * SIMS-8594 — the table presentation of the Pattern v2 config list.
 *
 * One of two interchangeable renderers beneath the shell (the other is the card view). The
 * shell owns search / filter / sort / pagination state; this component draws rows and reports
 * sort clicks, nothing more. Keeping it stateless is what lets AC1 switch views over the same
 * record set without losing the toolbar state (TestRail C17595).
 *
 * Spec — `Payers Pattern v2.dc.html`:
 *   header L231: height 48px, background --surface-tint, border-bottom 1px --divider,
 *                12px / 600, letter-spacing .04em, uppercase, colour --text-secondary
 *   body   L241: min-height 56px, border-bottom 1px --divider,
 *                hover background --surface-brand-soft
 *   tracks L231: 1.7fr 1.4fr 120px 110px 1.1fr 56px, gap 16px, padding 0 16px
 *
 * DELIBERATE DEVIATION: the prototype builds the list from CSS-grid <div>s; this renders a real
 * <table> with `table-layout: fixed` and <col> widths. The same hand-off tells the sibling
 * tables to "recreate as a real <table>", and a grid of divs gives assistive tech no row or
 * column relationship — the design's own §6 requires the screen to be operable and legible to
 * it. The <col> widths reproduce the grid proportions, so the visual result matches. Recorded
 * in the story's source manifest.
 *
 * Sort affordance follows `Roles Pattern v2.dc.html:721-733`; the Payers export has inert
 * <span> headers with no sort control (see the manifest's second-reader diffs).
 *
 * Colours bind to token NAMES via the theme, never hex: `--surface-1/2/3` differ per brand, so
 * a literal silently breaks JANUS (Epic plan D-5).
 */

export interface ConfigColumn<T> {
	/** Sort key; must match what the shell's `sortValue` understands. */
	key: string;
	/** Header label. Kept in sentence case — the uppercase is presentational. */
	header: string;
	/** Column width, in the design's own units (`1.7fr`, `120px`, …). */
	width: string;
	render: (row: T) => ReactNode;
	sortable?: boolean;
	/** Right-align numeric columns such as the member count. */
	align?: "left" | "right";
}

export interface ConfigTableProps<T> {
	rows: T[];
	columns: ConfigColumn<T>[];
	rowKey: (row: T) => string;
	sortKey: string;
	sortDir: SortDir;
	onSort: (key: string) => void;
	/** Row click — opens the full-page editor (SIMS-8614). */
	onRowClick?: (row: T) => void;
	ariaLabel?: string;
}

function ConfigTable<T>({
	rows,
	columns,
	rowKey,
	sortKey,
	sortDir,
	onSort,
	onRowClick,
	ariaLabel,
}: ConfigTableProps<T>) {
	/*
	 * The table is a CARD, which it was not: every Pattern v2 frame draws it on
	 * `--surface-0` inside a `--border-default` hairline at radius 12, with the header
	 * band clipped by that radius (`Actions Pattern v2.dc.html:264`). Rendered as a bare
	 * scroll box, the tinted header ran square into the page and the table had no edge
	 * of its own.
	 *
	 * `overflow: hidden` is what makes the corners actually round — without it the
	 * header's background paints over them. It also has to sit on the same element as
	 * the radius, hence one box rather than a wrapper.
	 */
	return (
		<Box
			sx={{
				overflow: "hidden",
				backgroundColor: "background.paper",
				border: "1px solid",
				borderColor: (theme) => theme.palette.outline.main,
				borderRadius: "0.75rem",
			}}
		>
			<Box sx={{ overflowX: "auto" }}>
				<Box
					component="table"
					aria-label={ariaLabel}
					sx={{
						width: "100%",
						borderCollapse: "collapse",
						// `fixed` is what makes <col> widths authoritative, so the fr/px proportions
						// from the design's grid hold instead of being recomputed from content.
						tableLayout: "fixed",
						"& thead th": {
							height: "3rem", // 48px
							backgroundColor: (theme) => theme.palette.surfaceTint.main,
							borderBottom: "1px solid",
							borderBottomColor: "divider",
							padding: "0 1rem",
							fontSize: "0.75rem", // 12px
							fontWeight: 600,
							letterSpacing: "0.04em",
							textTransform: "uppercase",
							color: "text.secondary",
							textAlign: "left",
							whiteSpace: "nowrap",
						},
						"& tbody td": {
							height: "3.5rem", // 56px — the design's min-height
							padding: "0 1rem",
							borderBottom: "1px solid",
							borderBottomColor: "divider",
							fontSize: "0.875rem",
							color: "text.primary",
						},
						// Hover, not zebra: the Pattern v2 list has no striping (verified — zero
						// nth-of-type rules in the prototype), unlike the SIMS-7504 framed tables.
						//
						// The design's hover is `--surface-brand-soft` (L241), which the design system
						// defines as the BRAND colour at 6% alpha — `rgba(82,25,123,0.06)` for SIMS,
						// `rgba(98,83,162,0.06)` for JANUS. MUI's `action.hover` is a neutral grey and
						// was visibly wrong. Derived from `primary.main` with alpha so it tracks the
						// tenant's brand, rather than aliased to `brandFaint`, which is a DIFFERENT
						// token (`--brand-primary-faint`, used by the Daily Journey selected day) —
						// SIMS-8458 records why conflating two roles that happen to look similar is a
						// bug waiting to happen.
						"& tbody tr:hover": {
							backgroundColor: (theme) =>
								alpha(theme.palette.primary.main, 0.06),
						},
					}}
				>
					<colgroup>
						{columns.map((c) => (
							<col key={c.key} style={{ width: c.width }} />
						))}
					</colgroup>
					<thead>
						<tr>
							{columns.map((c) => {
								const active = c.sortable === true && sortKey === c.key;
								return (
									<th
										key={c.key}
										scope="col"
										// aria-sort only on the column actually sorted; announcing it on
										// every column tells assistive tech nothing.
										aria-sort={
											active
												? sortDir === "asc"
													? "ascending"
													: "descending"
												: undefined
										}
										style={{
											textAlign: c.align === "right" ? "right" : "left",
										}}
									>
										{c.sortable === true && c.header !== "" ? (
											<TableSortLabel
												active={active}
												direction={active ? sortDir : "asc"}
												onClick={() => onSort(c.key)}
												sx={{
													font: "inherit",
													letterSpacing: "inherit",
													color: "inherit",
												}}
											>
												{c.header}
											</TableSortLabel>
										) : (
											c.header
										)}
									</th>
								);
							})}
						</tr>
					</thead>
					<tbody>
						{rows.map((row) => (
							<tr
								key={rowKey(row)}
								onClick={onRowClick ? () => onRowClick(row) : undefined}
								style={onRowClick ? { cursor: "pointer" } : undefined}
							>
								{columns.map((c) => (
									<td
										key={c.key}
										style={{
											textAlign: c.align === "right" ? "right" : "left",
										}}
									>
										{c.render(row)}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</Box>
			</Box>
		</Box>
	);
}

export default ConfigTable;
